import React, { MouseEvent } from 'react';
import { TTabLabelRenderer } from '../types';
import { useTabsContext } from '../util';
import { Icon, TIconName, isIconName } from '@apia/icons';
import { Checkbox, IconButton, SimpleButton } from '../../components';
import { getLabel } from '@apia/util';
import { observer } from 'mobx-react-lite';

function makeIcon(name: TIconName) {
  return function IconComponent() {
    return <Icon title="" name={name} />;
  };
}

export const DefaultTabsLabelRenderer: TTabLabelRenderer = observer(
  ({ tab }) => {
    const controller = useTabsContext();
    const accessibleName =
      tab.state.title?.trim() || tab.state.label?.trim() || tab.id;
    const ActualIcon = React.useMemo(() => {
      if (tab.state.icon) {
        if (isIconName(tab.state.icon)) {
          return makeIcon(tab.state.icon);
        }
        return tab.state.icon;
      }
      return function C() {
        return null;
      };
    }, [tab.state.icon]);

    const handleClose = React.useCallback(
      (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        controller?.closeTab(tab.id);
      },
      [controller, tab.id],
    );

    const handleMouseDown = React.useCallback(
      (ev: MouseEvent) => {
        if (ev.shiftKey && ev.button === 0) {
          tab.controller.openAll();
          return;
        }

        if (ev.button === 1) {
          tab.close();
        } else if (ev.button === 0) {
          tab.open(undefined, true);
        }
      },
      [tab],
    );

    return (
      <>
        <>
          {tab.controller.state.isMultiple && (
            <Checkbox
              aria-label={accessibleName}
              onChange={(ev) => {
                tab.toggle(ev.target.checked);
              }}
              checked={tab.state.isOpen}
            />
          )}
          {tab.state.isIconTab ? (
            <IconButton
              onMouseDown={handleMouseDown}
              className="tabs__item__button"
              as="div"
              icon={tab.state.icon as TIconName}
              variant="null"
              iconSize="Sm"
              size="Lg"
            />
          ) : (
            <SimpleButton
              onMouseDown={handleMouseDown}
              className="tabs__item__button"
              data-test-id={`tab-button-${tab.controller.id}-${tab.id}`}
              variant="null"
              as="div"
            >
              <ActualIcon />
              {tab.state.labelIcon}
              {tab.state.label}
            </SimpleButton>
          )}
        </>

        {tab.state.isClosable && (
          <IconButton
            as="div"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={handleClose}
            className="tabs__item__close"
            icon="Close"
            variant="null"
            title={
              getLabel('lblCloseSpecificTab', {
                text: { TOK1: tab.state.label },
              }).text
            }
            size="Lg"
            iconSize="Sm"
          />
        )}
      </>
    );
  },
);
